import { useState } from 'react';

import { NotebookPen } from 'lucide-react';

import { Button } from 'components/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from 'components/Dialog';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { Textarea } from 'components/Textarea';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { GlobalTranzNoteType } from 'lib/api/globalTranzNotes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

const MAX_NOTE_LENGTH = 10000;

type AddNoteDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  noteTypes: GlobalTranzNoteType[];
  noteTypesAreLoading: boolean;
  noteTypesError: boolean;
  isSaving: boolean;
  onSave: (noteTypeID: number, text: string) => Promise<boolean>;
  loadID?: number;
};

export function AddNoteDialog({
  open,
  onOpenChange,
  noteTypes,
  noteTypesAreLoading,
  noteTypesError,
  isSaving,
  onSave,
  loadID,
}: AddNoteDialogProps) {
  const [selectedNoteTypeID, setSelectedNoteTypeID] = useState('');
  const [noteText, setNoteText] = useState('');

  const resetAndClose = () => {
    setSelectedNoteTypeID('');
    setNoteText('');
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSaving) {
      return;
    }
    if (!nextOpen) {
      resetAndClose();
      return;
    }

    onOpenChange(true);
  };

  const handleSave = async () => {
    const noteTypeID = Number(selectedNoteTypeID);
    const text = noteText.trim();
    if (!noteTypeID || !text) {
      return;
    }

    if (await onSave(noteTypeID, text)) {
      resetAndClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='flex max-h-[90vh] max-w-md flex-col gap-0 overflow-hidden p-0'>
        <Flex
          align='center'
          gap='sm'
          className='bg-brand-main px-4 py-3 text-neutral-50 border-b rounded-t-lg [&>svg]:stroke-white!'
        >
          <NotebookPen className='h-5 w-5' />
          <DialogTitle className='text-neutral-50'>Add Note</DialogTitle>
          <DialogDescription className='sr-only'>
            Select a note type and enter the note text.
          </DialogDescription>
        </Flex>

        <Flex
          direction='col'
          gap='md'
          className='min-h-0 w-full flex-1 overflow-y-auto px-4 py-4'
        >
          <Flex direction='col' gap='sm' className='w-full'>
            <Label name='globalTranzNoteType' showAIHint={false}>
              Note Type
            </Label>
            <Select
              value={selectedNoteTypeID}
              onValueChange={setSelectedNoteTypeID}
              disabled={noteTypesAreLoading || noteTypesError || isSaving}
            >
              <SelectTrigger
                id='globalTranzNoteType'
                aria-describedby={
                  noteTypesError ? 'globalTranzNoteTypeError' : undefined
                }
                className='w-full'
              >
                <SelectValue
                  placeholder={
                    noteTypesAreLoading
                      ? 'Loading note types...'
                      : 'Select Note Type'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {noteTypes.map((noteType) => (
                  <SelectItem key={noteType.id} value={String(noteType.id)}>
                    {noteType.description || noteType.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {noteTypesError && (
              <Typography
                id='globalTranzNoteTypeError'
                role='alert'
                variant='body-xs'
                className='text-error-500'
              >
                Note types could not be loaded. Close and reopen the dialog to
                retry.
              </Typography>
            )}
          </Flex>

          <Flex direction='col' gap='sm' className='w-full'>
            <Label name='globalTranzNoteText' showAIHint={false}>
              Note
            </Label>
            <Textarea
              id='globalTranzNoteText'
              value={noteText}
              onChange={(event) => setNoteText(event.target.value)}
              placeholder='Enter Text'
              maxLength={MAX_NOTE_LENGTH}
              rows={5}
              disabled={isSaving}
              className='w-full'
            />
            <Typography
              variant='body-xs'
              className='text-neutral-500 text-right'
            >
              {noteText.length}/{MAX_NOTE_LENGTH}
            </Typography>
          </Flex>
        </Flex>

        <Flex
          justify='end'
          gap='sm'
          className='w-full shrink-0 rounded-b-lg border-t border-neutral-300 bg-neutral-100 px-4 py-3'
        >
          <Button
            buttonNamePosthog={null}
            type='button'
            variant='outline'
            size='sm'
            disabled={isSaving}
            onClick={resetAndClose}
            className='flex-1 text-sm'
          >
            Cancel
          </Button>
          <Button
            buttonNamePosthog={ButtonNamePosthog.SubmitNote}
            type='button'
            size='sm'
            disabled={
              isSaving || !selectedNoteTypeID || noteText.trim().length === 0
            }
            onClick={handleSave}
            logProperties={{ loadID }}
            className='flex-1 text-sm'
          >
            {isSaving ? <ButtonLoader /> : 'Save'}
          </Button>
        </Flex>
      </DialogContent>
    </Dialog>
  );
}
